'use client'

import { useEffect } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'

import {
  createBundleSummary,
  transformBundleSummaryDto,
} from '@marketplace-web/bundles/items-bundle-data'

type Props = {
  selectedItems: Array<ProductItemModel>
  isSelectedItemsUpdatedFromUrl: boolean
  sellerId: number
}

const useProfileBundles = ({ selectedItems, isSelectedItemsUpdatedFromUrl, sellerId }: Props) => {
  const bundlingSellerProfileAbTest = useAbTest('bundling_seller_profile')
  const isBundlingSellerProfileAbTestOn = bundlingSellerProfileAbTest?.variant === 'on'

  const {
    fetch: fetchSummary,
    transformedData: bundleSummary,
    isLoading: isBundleSummaryLoading,
  } = useFetch(createBundleSummary, response => transformBundleSummaryDto(response.bundle_summary))

  const fetchDebouncedSummary = useDebounce(
    async ({ itemIds }) => {
      await fetchSummary({ itemIds, sellerId })
    },
    500,
    false,
  )

  useEffect(() => {
    if (!isBundlingSellerProfileAbTestOn) return
    if (!isSelectedItemsUpdatedFromUrl) return

    const itemIds = selectedItems.map(({ id }) => id)

    fetchDebouncedSummary({ itemIds })
  }, [
    fetchDebouncedSummary,
    isSelectedItemsUpdatedFromUrl,
    selectedItems,
    isBundlingSellerProfileAbTestOn,
  ])

  return {
    bundleSummary,
    isBundleSummaryLoading,
  }
}

export default useProfileBundles
